import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Module-level singleton so multiple component instances share the same prompt state
let globalDeferredPrompt: BeforeInstallPromptEvent | null = null;
let globalInstalled = false;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

export function usePwaInstall() {
  const [, setDeferredPrompt] = useState(globalDeferredPrompt);
  const [, setIsInstalled] = useState(globalInstalled);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const sync = () => {
      setDeferredPrompt(globalDeferredPrompt);
      setIsInstalled(globalInstalled);
    };
    listeners.add(sync);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      globalInstalled = true;
      notifyListeners();
    }

    const handler = (e: Event) => {
      e.preventDefault();
      globalDeferredPrompt = e as BeforeInstallPromptEvent;
      notifyListeners();
    };

    const installedHandler = () => {
      globalDeferredPrompt = null;
      globalInstalled = true;
      notifyListeners();
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
      listeners.delete(sync);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!globalDeferredPrompt) return;
    await globalDeferredPrompt.prompt();
    const { outcome } = await globalDeferredPrompt.userChoice;
    if (outcome === 'accepted') {
      globalDeferredPrompt = null;
      notifyListeners();
    }
  }, []);

  return { isInstallable: !!globalDeferredPrompt, isInstalled: globalInstalled, promptInstall };
}